// import api from "@/lib/api/axios"; // Commented out for now
import {
  Post,
  CreatePostData,
  UpdatePostData,
  PostsResponse,
} from "@/types/posts";
import { type Comment } from "@/types"; // Import Comment type
import { mockPosts } from "@/lib/mock-data/posts";

// const BASE_URL = "/api/posts/"; // Commented out for now

// Define a specific return type for the transformed getPosts response
interface TransformedPostsResponse {
  data: Post[];
  meta: PostsResponse["meta"];
}

export const postsApi = {
  /**
   * Get list of posts with pagination, ordering, search, and category filter
   *
   * @param page Page number
   * @param ordering Field to order by (e.g., 'created_at', '-created_at')
   * @param search Search query
   * @param category Category filter - can be single string or array of strings (e.g., 'help', ['help', 'alert'])
   */
  getPosts: async (
    page = 1,
    ordering?: string,
    search?: string,
    category?: string | string[],
  ): Promise<TransformedPostsResponse> => {
    // Mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        // Start with all posts
        let filteredPosts = [...mockPosts];

        // Filter by category if provided
        if (category) {
          if (Array.isArray(category)) {
            // Multiple categories - show posts that match any of the categories
            filteredPosts = filteredPosts.filter(
              (post) =>
                typeof post.category === "string" &&
                category.includes(post.category),
            );
          } else {
            // Single category
            filteredPosts = filteredPosts.filter(
              (post) => post.category === category,
            ); // This comparison is fine even if post.category is undefined
          }
        }

        // Filter by search if provided
        if (search) {
          const searchLower = search.toLowerCase();

          // Handle OR search for tags
          if (search.includes(" OR ")) {
            const searchTerms = search
              .split(" OR ")
              .map((term) => term.trim().toLowerCase());
            filteredPosts = filteredPosts.filter((post) => {
              return searchTerms.some(
                (term) =>
                  post.content.toLowerCase().includes(term) ||
                  (post.title && post.title.toLowerCase().includes(term)) ||
                  (post.category && post.category.toLowerCase().includes(term)),
              );
            });
          } else {
            // Regular search
            filteredPosts = filteredPosts.filter(
              (post) =>
                post.content.toLowerCase().includes(searchLower) ||
                (post.title &&
                  post.title.toLowerCase().includes(searchLower)) ||
                (post.category &&
                  post.category.toLowerCase().includes(searchLower)),
            );
          }
        }

        // Sort by ordering if provided
        if (ordering) {
          const isDesc = ordering.startsWith("-");
          const field = isDesc ? ordering.substring(1) : ordering;

          filteredPosts.sort((a, b) => {
            // @ts-expect-error - Dynamic field access
            const valueA = a[field];
            // @ts-expect-error - Dynamic field access
            const valueB = b[field];

            if (valueA < valueB) return isDesc ? 1 : -1;
            if (valueA > valueB) return isDesc ? -1 : 1;
            return 0;
          });
        } else {
          // Default sort by createdAt desc
          filteredPosts.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          );
        }

        // Paginate
        const pageSize = 10;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        const paginatedPosts = filteredPosts.slice(startIndex, endIndex);

        resolve({
          data: paginatedPosts,
          meta: {
            currentPage: page,
            totalPages: Math.ceil(filteredPosts.length / pageSize),
          },
        });
      }, 500); // Simulate network delay
    });
  },

  /**
   * Get a single post by ID
   */
  getPost: async (id: string): Promise<Post> => {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const post = mockPosts.find((p) => p.id === id);
        if (post) {
          resolve(post);
        } else {
          reject(new Error("Post not found"));
        }
      }, 300);
    });
  },

  /**
   * Create a new post
   */
  createPost: async (data: CreatePostData): Promise<Post> => {
    // Mock implementation
    return new Promise((resolve) => {
      setTimeout(() => {
        // Create a new post with mock data
        const newPost: Post = {
          id: `mock-${Date.now()}`,
          author: {
            id: "999",
            username: "currentuser",
            display_name: "Current User",
            avatar_url: "/avatars/avatar.svg",
            is_staff: false,
            is_verified: false,
            location: "Hà Nội",
          },
          community: null,
          type: "regular",
          category: data.category || "regular",
          title: data.title || null,
          content: data.content,
          location: "Hà Nội",
          status: "active",
          media: data.media
            ? data.media.map((file) => ({
                type: file.type.startsWith("image/") ? "image" : "video",
                url: URL.createObjectURL(file),
              }))
            : [],
          tags: data.tags || [],
          is_featured: false,
          is_edited: false,
          edited_at: null,
          view_count: 0,
          comments_count: 0,
          reactions_count: 0,
          participants_count: 0,
          is_liked: false,
          latest_comments: [],
          media_preview: [],
          createdAt: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          start_date: null,
          end_date: null,
          max_participants: null,
          helper: null,
          due_date: null,
        };

        // Add to mock posts at the beginning
        mockPosts.unshift(newPost);
        resolve(newPost);
      }, 700);
    });
  },

  /**
   * Update a post (full update)
   * Uses PUT method as specified in the OpenAPI spec
   */
  updatePost: async (id: string, data: UpdatePostData): Promise<Post> => {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockPosts.findIndex((p) => p.id === id);
        if (index !== -1) {
          mockPosts[index] = {
            ...mockPosts[index],
            ...data,
            is_edited: true,
            edited_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          resolve(mockPosts[index]);
        } else {
          reject(new Error("Post not found"));
        }
      }, 500);
    });
  },

  /**
   * Partially update a post
   * Uses PATCH method as specified in the OpenAPI spec
   */
  patchPost: async (
    id: string,
    data: Partial<UpdatePostData>,
  ): Promise<Post> => {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockPosts.findIndex((p) => p.id === id);
        if (index !== -1) {
          mockPosts[index] = {
            ...mockPosts[index],
            ...data,
            is_edited: true,
            edited_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          resolve(mockPosts[index]);
        } else {
          reject(new Error("Post not found"));
        }
      }, 500);
    });
  },

  /**
   * Delete a post
   */
  deletePost: async (id: string): Promise<void> => {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockPosts.findIndex((p) => p.id === id);
        if (index !== -1) {
          mockPosts.splice(index, 1);
          resolve();
        } else {
          reject(new Error("Post not found"));
        }
      }, 500);
    });
  },

  /**
   * Like a post (Note: This endpoint is not in the OpenAPI spec but is retained for compatibility)
   */
  likePost: async (id: string): Promise<Post> => {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockPosts.findIndex((p) => p.id === id);
        if (index !== -1) {
          mockPosts[index] = {
            ...mockPosts[index],
            is_liked: true,
            reactions_count: mockPosts[index].reactions_count + 1,
          };
          resolve(mockPosts[index]);
        } else {
          reject(new Error("Post not found"));
        }
      }, 300);
    });
  },

  /**
   * Unlike a post (Note: This endpoint is not in the OpenAPI spec but is retained for compatibility)
   */
  unlikePost: async (id: string): Promise<Post> => {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockPosts.findIndex((p) => p.id === id);
        if (index !== -1) {
          mockPosts[index] = {
            ...mockPosts[index],
            is_liked: false,
            reactions_count: Math.max(0, mockPosts[index].reactions_count - 1),
          };
          resolve(mockPosts[index]);
        } else {
          reject(new Error("Post not found"));
        }
      }, 300);
    });
  },

  /**
   * Create a comment on a post (Note: This endpoint is not in the OpenAPI spec but is retained for compatibility)
   */
  createComment: async (postId: string, content: string): Promise<Comment> => {
    // Mock implementation
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        const index = mockPosts.findIndex((p) => p.id === postId);
        if (index !== -1) {
          mockPosts[index] = {
            ...mockPosts[index],
            comments_count: mockPosts[index].comments_count + 1,
          };
          // Return a mock comment
          resolve({
            id: `comment-${Date.now()}`,
            postId: postId,
            authorId: "current-user",
            author: {
              id: "current-user",
              name: "Current User",
              username: "currentuser", // Added username to match User type
              avatarUrl: "/avatars/avatar.svg", // Added avatarUrl
              is_staff: false,
              is_verified: false,
              location: "Hanoi",
            },
            content,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            likes: 0,
            isLiked: false,
            replies: [],
            parentId: undefined,
            // authorId: "current-user", // Removed duplicate
          } as Comment); // Cast to Comment type
        } else {
          reject(new Error("Post not found"));
        }
      }, 500);
    });
  },
};

export default postsApi;
